import sha256 from 'crypto-js/sha256';

import { Cacheable, CacheableItem } from '@/lib/cacheable/types';

export class InMemoryCacheable implements Cacheable {
  constructor(
    private readonly cacheDisabled: boolean,
    private readonly map = new Map<string, CacheableItem>()
  ) {}

  _prepareKey(key: string) {
    return sha256(key).toString();
  }

  get(key: string) {
    key = this._prepareKey(key);
    if (!this.map.has(key)) {
      return undefined;
    }

    return this.map.get(key)?.value;
  }

  set(
    key: string,
    value: CacheableItem['value'],
    expiresAt?: CacheableItem['expiresAt']
  ) {
    if (this.cacheDisabled) return;

    key = this._prepareKey(key);

    this.map.set(key, {
      value,
      expiresAt,
    });
  }

  delete(key: string) {
    key = this._prepareKey(key);
    this.map.delete(key);
  }

  has(key: string) {
    key = this._prepareKey(key);
    const exists = this.map.has(key);

    if (!exists) {
      return false;
    }

    const struct = this.map.get(key);
    const expiresAt: number | undefined =
      struct?.expiresAt instanceof Date
        ? struct.expiresAt.getTime()
        : struct?.expiresAt;

    if (expiresAt && expiresAt < Date.now()) {
      this.map.delete(key);
      return false;
    }

    return true;
  }
  // no need to _prepareKey because we're using the methods above that already do that.
  async remember<T>(
    key: string,
    callback: () => Promise<T>,
    expiresAt?: CacheableItem['expiresAt']
  ): Promise<T> {
    if (this.has(key)) {
      return this.get(key);
    }

    const promise = callback();
    this.set(key, promise, expiresAt);
    const value = await promise;

    this.set(key, value, expiresAt);

    return value;
  }

  clear() {
    this.map.clear();
  }
}
